import { useEffect, useRef, useState } from "react";
import api, { API_BASE } from "@/lib/api";
import { toast } from "sonner";
import { useAuth, can } from "@/lib/auth";
import { useSettings } from "@/lib/settings";
import { compressDataUrlBeforeUpload, COMPRESSION_FAILED_MESSAGE } from "@/utils/imageCompression";
import { Pen, Eraser, MapPin, Type, Trash2, Save, RotateCcw } from "lucide-react";

const COLORS = ["#E76F51", "#8A9A86", "#457B9D", "#D4A373", "#2D3A33"];

export default function MappingCanvas({ visit, onSaved }) {
  const { user } = useAuth();
  const { settings } = useSettings();
  const TEMPLATES = settings?.mapping_templates || {};
  const editable = can(user, "edit_mapping");
  const [mapType, setMapType] = useState("face");
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(COLORS[0]);
  const [size, setSize] = useState(3);
  const [penOpacity, setPenOpacity] = useState(1);
  const [strokes, setStrokes] = useState([]); // [{tool, color, size, points:[{x,y}]}]
  const [markers, setMarkers] = useState([]); // [{x,y,label,color}]
  const [labelInput, setLabelInput] = useState("0.5 ml");
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const cur = useRef(null);

  const tpl = TEMPLATES[mapType];

  // Render
  useEffect(() => {
    const c = canvasRef.current; if (!c) return;
    if (!tpl?.svg && !tpl?.image_path) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    ctx.fillStyle = "#FFFFFF"; ctx.fillRect(0, 0, c.width, c.height);

    const renderOverlay = () => {
      // strokes
      strokes.forEach((s) => {
        if (!s || !s.points) return;
        ctx.strokeStyle = s.color; ctx.lineWidth = s.size; ctx.lineCap = "round"; ctx.lineJoin = "round";
        if (s.tool === "eraser") {
          ctx.strokeStyle = "#FFFFFF";
          ctx.lineWidth = s.size * 4;
          ctx.globalAlpha = 1;
        } else {
          ctx.globalAlpha = s.opacity ?? 1;
        }
        if (s.points.length < 2) return;
        ctx.beginPath();
        ctx.moveTo(s.points[0].x, s.points[0].y);
        for (let i = 1; i < s.points.length; i++) ctx.lineTo(s.points[i].x, s.points[i].y);
        ctx.stroke();
        ctx.globalAlpha = 1;
      });
      // markers
      markers.forEach((m) => {
        ctx.fillStyle = m.color;
        ctx.beginPath(); ctx.arc(m.x, m.y, 7, 0, Math.PI * 2); ctx.fill();
        ctx.fillStyle = "#FFFFFF";
        ctx.beginPath(); ctx.arc(m.x, m.y, 3, 0, Math.PI * 2); ctx.fill();
        if (m.label) {
          ctx.font = "12px DM Sans, sans-serif";
          ctx.fillStyle = "#2D3A33";
          ctx.fillRect(m.x + 10, m.y - 18, ctx.measureText(m.label).width + 10, 18);
          ctx.fillStyle = "#FFFFFF";
          ctx.fillText(m.label, m.x + 15, m.y - 5);
        }
      });
    };

    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      // letterbox-fit the template inside the canvas keeping aspect ratio
      const ratio = Math.min(c.width / img.width, c.height / img.height);
      const w = img.width * ratio, h = img.height * ratio;
      const ox = (c.width - w) / 2, oy = (c.height - h) / 2;
      ctx.drawImage(img, ox, oy, w, h);
      renderOverlay();
    };
    img.onerror = () => { renderOverlay(); };
    if (tpl.image_path) {
      img.src = `${API_BASE}/files/${tpl.image_path}`;
    } else {
      img.src = "data:image/svg+xml;utf8," + encodeURIComponent(tpl.svg);
    }
  }, [strokes, markers, mapType, tpl]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return {
      x: ((t?.clientX ?? e.clientX) - r.left) * (c.width / r.width),
      y: ((t?.clientY ?? e.clientY) - r.top) * (c.height / r.height),
    };
  };

  const handleStart = (e) => {
    if (!editable) return;
    e.preventDefault();
    const p = pos(e);
    if (tool === "marker") {
      setMarkers((m) => [...m, { x: p.x, y: p.y, label: labelInput, color }]);
      setDirty(true);
      return;
    }
    drawing.current = true;
    cur.current = { tool, color, size, opacity: tool === "pen" ? penOpacity : 1, points: [p] };
    setStrokes((s) => [...s, cur.current]);
    setDirty(true);
  };
  const handleMove = (e) => {
    if (!drawing.current || !cur.current) return;
    e.preventDefault();
    cur.current.points.push(pos(e));
    const snapshot = { ...cur.current, points: [...cur.current.points] };
    setStrokes((s) => (s.length === 0 ? [snapshot] : [...s.slice(0, -1), snapshot]));
    setDirty(true);
  };
  const handleEnd = () => {
    if (cur.current) {
      // commit final snapshot to ensure no lingering shared reference
      const final = { ...cur.current, points: [...cur.current.points] };
      setStrokes((s) => (s.length === 0 ? [final] : [...s.slice(0, -1), final]));
    }
    drawing.current = false;
    cur.current = null;
  };

  const undo = () => {
    if (markers.length > 0) setMarkers((m) => m.slice(0, -1));
    else setStrokes((s) => s.slice(0, -1));
    setDirty(true);
  };

  const clearAll = () => { setStrokes([]); setMarkers([]); setDirty(true); };

  const save = async () => {
    const c = canvasRef.current;
    const dataUrl = c.toDataURL("image/png");
    setSaving(true);
    try {
      const compressed = await compressDataUrlBeforeUpload(dataUrl);
      await api.post(`/visits/${visit.id}/mappings`, {
        map_type: mapType,
        image_data: compressed.dataUrl,
        raw_json: { strokes, markers },
        notes: "",
      });
      toast.success("Mapping saved");
      onSaved?.();
      clearAll();
      setDirty(false);
    } catch (e) {
      toast.error(e?.message || COMPRESSION_FAILED_MESSAGE);
    } finally {
      setSaving(false);
    }
  };

  const delMap = async (id) => {
    try { await api.delete(`/visits/${visit.id}/mappings/${id}`); onSaved?.(); } catch {}
  };

  const canvasW = mapType === "face" ? 480 : 400;
  const canvasH = mapType === "face" ? 560 : 520;

  return (
    <div className="space-y-5">
      <p className="text-sm text-[#5C6C62]">
        Mark treatment areas, injection points, or concerns directly on the template.
      </p>
      {editable && (
        <div className="bl-card p-5">
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <select className="bl-input w-auto" value={mapType} onChange={(e)=>{ setMapType(e.target.value); clearAll(); setDirty(false); }} data-testid="map-template-select">
              {Object.entries(TEMPLATES).map(([k, v]) => (
                <option key={k} value={k}>{v.label || k}</option>
              ))}
            </select>
            <div className="flex items-center gap-1 bg-[#F3F1EB] rounded-lg p-1">
              <button onClick={()=>setTool("pen")} className={`p-2 rounded-md ${tool==="pen" ? "bg-white shadow-sm" : ""}`} data-testid="tool-pen"><Pen className="w-4 h-4" /></button>
              <button onClick={()=>setTool("eraser")} className={`p-2 rounded-md ${tool==="eraser" ? "bg-white shadow-sm" : ""}`} data-testid="tool-eraser"><Eraser className="w-4 h-4" /></button>
              <button onClick={()=>setTool("marker")} className={`p-2 rounded-md ${tool==="marker" ? "bg-white shadow-sm" : ""}`} data-testid="tool-marker"><MapPin className="w-4 h-4" /></button>
            </div>
            <div className="flex items-center gap-1.5">
              {COLORS.map((c) => (
                <button key={c} onClick={()=>setColor(c)} className={`w-7 h-7 rounded-full border-2 ${color === c ? "border-[#2D3A33]" : "border-transparent"}`} style={{ background: c }} aria-label={c} />
              ))}
            </div>
            <input type="range" min="1" max="10" value={size} onChange={(e)=>setSize(parseInt(e.target.value))} className="w-24" aria-label="Pen size" />
            {tool === "pen" && (
              <div className="flex items-center gap-2" data-testid="map-pen-opacity">
                <span className="text-xs text-[#5C6C62] whitespace-nowrap">Opacity</span>
                <input
                  type="range"
                  min="10"
                  max="100"
                  value={Math.round(penOpacity * 100)}
                  onChange={(e) => setPenOpacity(parseInt(e.target.value, 10) / 100)}
                  className="w-24"
                  aria-label="Pen opacity"
                />
              </div>
            )}
            {tool === "marker" && (
              <div className="flex items-center gap-2">
                <Type className="w-4 h-4 text-[#5C6C62]" />
                <input className="bl-input w-32 py-1.5" placeholder="Dosage label" value={labelInput} onChange={(e)=>setLabelInput(e.target.value)} data-testid="marker-label" />
              </div>
            )}
            <div className="ml-auto flex flex-wrap items-center gap-2">
              {dirty && (
                <span className="text-xs font-medium text-[#B45309] bg-[#FEF3C7] px-2 py-1 rounded-md" data-testid="mapping-unsaved">
                  Unsaved changes
                </span>
              )}
              <button onClick={undo} className="bl-btn-ghost text-sm inline-flex items-center gap-1.5"><RotateCcw className="w-3.5 h-3.5" /> Undo</button>
              <button onClick={clearAll} className="bl-btn-ghost text-sm inline-flex items-center gap-1.5"><Trash2 className="w-3.5 h-3.5" /> Clear</button>
              <button onClick={save} disabled={saving} className="bl-btn-primary text-sm inline-flex items-center gap-1.5" data-testid="map-save">
                <Save className="w-3.5 h-3.5" /> {saving ? "Optimizing photo..." : "Save mapping"}
              </button>
            </div>
          </div>

          <div className="bg-[#FBF8EF] rounded-xl border border-[#EAE6D7] flex justify-center items-center p-6 min-h-[320px]">
            <canvas
              ref={canvasRef}
              width={canvasW}
              height={canvasH}
              className="bg-white rounded-lg border border-[#EAE6D7] touch-none shadow-sm"
              style={{ width: "min(100%, 480px)", height: "auto", aspectRatio: `${canvasW} / ${canvasH}` }}
              onMouseDown={handleStart} onMouseMove={handleMove} onMouseUp={handleEnd} onMouseLeave={handleEnd}
              onTouchStart={handleStart} onTouchMove={handleMove} onTouchEnd={handleEnd}
              data-testid="mapping-canvas"
            />
          </div>
        </div>
      )}

      {/* Saved mappings */}
      <div>
        <div className="label-eyebrow mb-3">Saved mappings</div>
        {(visit.mappings || []).length === 0 ? (
          <div className="bl-card p-8 text-center text-[#5C6C62]">No mappings yet</div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid="mappings-list">
            {visit.mappings.map(m => (
              <div key={m.id} className="bl-card overflow-hidden group">
                <div className="bg-[#F3F1EB] aspect-[3/4] relative">
                  <img src={m.image_data} alt={m.map_type} className="w-full h-full object-contain" />
                  {editable && (
                    <button onClick={()=>delMap(m.id)} className="absolute top-2 right-2 p-1.5 rounded-lg bg-white/90 text-[#B14A2C] opacity-0 group-hover:opacity-100 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <div className="p-3">
                  <div className="text-sm font-medium capitalize">{m.map_type.replace("_"," ")}</div>
                  <div className="text-xs text-[#5C6C62]">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
